import styled from "@emotion/styled";
import { Skeleton, Typography } from "@mui/material";
import type { ListCMAResponseItemDto } from "@cma/shared/apis/comps/cma/generated";
import { formatPresentationTitle } from "../presentation-helpers";
import { PresentationCard } from "./PresentationCard";

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 1rem;

  ${(p) => p.theme.media.laptopDown} {
    gap: 1rem;
    grid-template-columns: repeat(2, 1fr);
  }
`;

const SkeletonCard = styled.div`
  border-radius: 0.75rem;
  overflow: hidden;
  border: 1px solid ${(p) => p.theme.colors.border2};
`;

const SkeletonImage = styled(Skeleton)`
  width: 100%;
  height: 140px;
`;

const TotalCount = styled(Typography)`
  margin-bottom: 0.5rem;
  color: ${(p) => p.theme.text.colors.brand};
`;

const EmptyState = styled(Typography)`
  text-align: center;
  padding: 4rem 0;
  color: ${(p) => p.theme.text.colors.secondary};
`;

type PresentationsListProps = {
  presentations: ListCMAResponseItemDto[];
  isLoading: boolean;
  totalItems: number;
  isFetchingMore: boolean;
  hasMore: boolean;
  sentinelRef: React.RefCallback<HTMLDivElement>;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onCopyLink: (id: string) => void;
  onDelete: (id: string) => void;
};

export function PresentationsList(props: PresentationsListProps) {
  const {
    presentations,
    isLoading,
    totalItems,
    isFetchingMore,
    hasMore,
    sentinelRef,
    onView,
    onEdit,
    onCopyLink,
    onDelete,
  } = props;

  if (isLoading && presentations.length === 0) {
    return (
      <Grid>
        {Array(8)
          .fill(null)
          .map((_, i) => (
            <SkeletonCard key={i}>
              <SkeletonImage variant="rectangular" />
              <Skeleton variant="text" sx={{ mt: 1, mx: 1 }} />
              <Skeleton variant="text" width="60%" sx={{ mb: 1, mx: 1 }} />
            </SkeletonCard>
          ))}
      </Grid>
    );
  }

  if (!isLoading && presentations.length === 0) {
    return <EmptyState>No presentations found.</EmptyState>;
  }

  return (
    <>
      <TotalCount variant="subtitle1">
        {totalItems} {totalItems === 1 ? "Presentation" : "Presentations"}
      </TotalCount>
      <Grid>
        {presentations.map((cma) => {
          const title = formatPresentationTitle(cma);
          return (
            <PresentationCard
              key={cma.id}
              id={cma.id}
              title={title}
              clientDisplayName={cma.clientDisplayName}
              coverImage={cma.coverImage}
              updatedDate={cma.updatedDate}
              onView={onView}
              onEdit={onEdit}
              onCopyLink={onCopyLink}
              onDelete={onDelete}
            />
          );
        })}
        {isFetchingMore &&
          Array(4)
            .fill(null)
            .map((_, i) => (
              <SkeletonCard key={`more-${i}`}>
                <SkeletonImage variant="rectangular" />
                <Skeleton variant="text" sx={{ mt: 1, mx: 1 }} />
                <Skeleton variant="text" width="60%" sx={{ mb: 1, mx: 1 }} />
              </SkeletonCard>
            ))}
      </Grid>
      <div ref={sentinelRef} />
      {!hasMore && !isFetchingMore && presentations.length > 4 && (
        <Typography variant="body2" textAlign="center" py={4}>
          You&apos;ve seen all your presentations.
        </Typography>
      )}
    </>
  );
}
